import { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Paper, Stack, Typography, Button, Chip, Alert, CircularProgress, Divider,
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import { api } from '../api/client';
import type { AccessEventDto } from '../api/types';

const MAX_BYTES = 10 * 1024 * 1024;
const ACCEPTED = ['image/jpeg', 'image/png'];

export function UploadEventPage() {
  const nav = useNavigate();
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [file, setFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<AccessEventDto | null>(null);
  const [error, setError] = useState<string | null>(null);

  const choose = (f: File | null) => {
    setError(null);
    if (!f) { setFile(null); setPreviewUrl(null); return; }
    if (!ACCEPTED.includes(f.type)) { setError('Solo JPEG o PNG.'); return; }
    if (f.size > MAX_BYTES) { setError('El archivo excede 10MB.'); return; }
    setFile(f);
    setPreviewUrl(URL.createObjectURL(f));
  };

  const onDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    choose(e.dataTransfer.files?.[0] ?? null);
  };

  const reset = () => {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setFile(null);
    setPreviewUrl(null);
    setResult(null);
    setError(null);
    if (inputRef.current) inputRef.current.value = '';
  };

  const submit = async () => {
    if (!file) return;
    setBusy(true);
    setError(null);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await api.post<AccessEventDto>('/access-events/upload', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setResult(res.data);
    } catch (e: unknown) {
      const ax = e as { response?: { data?: { message?: string } } };
      setError(ax.response?.data?.message ?? 'Error al procesar la imagen.');
    } finally {
      setBusy(false);
    }
  };

  const statusColor = (s?: string) =>
    s === 'PROCESSED' ? 'success'
    : s === 'OVERRIDDEN' ? 'primary'
    : s === 'MANUAL_REVIEW' ? 'warning'
    : s === 'FAILED' ? 'error' : 'default';

  return (
    <Box>
      <Typography variant="h4" sx={{ mb: 2 }}>Subir lectura manual</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Sube una foto de placa. La IA leerá la placa y el sistema registrará la entrada o salida automáticamente.
      </Typography>

      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
        <Paper sx={{ p: 3, flex: 1, minWidth: 320 }}>
          <Typography variant="h6" sx={{ mb: 2 }}>Imagen</Typography>
          <Box
            onDragOver={(e) => e.preventDefault()}
            onDrop={onDrop}
            onClick={() => inputRef.current?.click()}
            sx={{
              border: '2px dashed', borderColor: 'divider', borderRadius: 1,
              p: 3, textAlign: 'center', cursor: 'pointer', bgcolor: 'action.hover',
              minHeight: 280, display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}
          >
            {previewUrl ? (
              <img src={previewUrl} alt="preview" style={{ maxWidth: '100%', maxHeight: 320, objectFit: 'contain' }} />
            ) : (
              <Stack alignItems="center" spacing={1}>
                <CloudUploadIcon fontSize="large" color="action" />
                <Typography variant="body2">Arrastra una foto aquí o haz click para elegir</Typography>
                <Typography variant="caption" color="text.secondary">JPEG o PNG, máx 10MB</Typography>
              </Stack>
            )}
          </Box>
          <input
            ref={inputRef}
            type="file"
            accept="image/jpeg,image/png"
            style={{ display: 'none' }}
            onChange={(e) => choose(e.target.files?.[0] ?? null)}
          />

          {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}

          <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
            <Button
              variant="contained"
              onClick={submit}
              disabled={!file || busy}
              startIcon={busy ? <CircularProgress size={16} /> : <CloudUploadIcon />}
            >
              {busy ? 'Procesando...' : 'Procesar'}
            </Button>
            <Button startIcon={<RestartAltIcon />} onClick={reset} disabled={busy}>Limpiar</Button>
          </Stack>
        </Paper>

        <Paper sx={{ p: 3, flex: 1, minWidth: 320 }}>
          <Typography variant="h6" sx={{ mb: 2 }}>Resultado</Typography>
          {!result && !busy && <Typography variant="body2" color="text.secondary">Aún no hay resultado.</Typography>}
          {busy && (
            <Stack alignItems="center" spacing={1} sx={{ py: 4 }}>
              <CircularProgress />
              <Typography variant="body2" color="text.secondary">Llamando a Bedrock...</Typography>
            </Stack>
          )}
          {result && (
            <Stack spacing={1.5}>
              <Stack direction="row" spacing={1}>
                <Chip label={result.status} color={statusColor(result.status)} />
                {result.eventType && (
                  <Chip
                    label={result.eventType === 'ENTRY' ? 'Entrada' : result.eventType === 'EXIT' ? 'Salida' : 'No resuelto'}
                    color={result.eventType === 'ENTRY' ? 'success' : result.eventType === 'EXIT' ? 'info' : 'warning'}
                  />
                )}
              </Stack>

              <Box>
                <Typography variant="caption" color="text.secondary">Placa leída</Typography>
                <Typography variant="h5" sx={{ fontFamily: 'monospace' }}>
                  {result.detectedPlate ?? '(no legible)'}
                </Typography>
              </Box>

              <Box>
                <Typography variant="caption" color="text.secondary">Confianza</Typography>
                <Typography variant="body2">
                  {result.confidence != null ? Number(result.confidence).toFixed(3) : '—'}
                </Typography>
              </Box>

              <Divider />
              <Typography variant="subtitle2">Camioneta</Typography>
              {result.vehicle ? (
                <Stack direction="row" spacing={1} alignItems="center">
                  <Chip label={result.vehicle.plateDisplay} sx={{ fontFamily: 'monospace' }} />
                  <Typography variant="body2">{result.vehicle.driverName ?? '(sin chofer)'}</Typography>
                </Stack>
              ) : (
                <Typography variant="body2" color="text.secondary">No coincidió con catálogo</Typography>
              )}

              {result.errorCode && (
                <Alert severity="warning">
                  <strong>{result.errorCode}</strong>: {result.errorMessage ?? 'Sin detalle'}
                </Alert>
              )}

              <Stack direction="row" spacing={1} sx={{ pt: 1 }}>
                <Button size="small" onClick={() => nav(`/history/${result.id}`)}>Ver detalle</Button>
                {result.status === 'MANUAL_REVIEW' && (
                  <Button size="small" onClick={() => nav('/admin/manual-review')}>Ir a revisión manual</Button>
                )}
                <Button size="small" onClick={reset}>Subir otra</Button>
              </Stack>
            </Stack>
          )}
        </Paper>
      </Stack>
    </Box>
  );
}
